/**
 * MenuManager — staff (manager/admin/owner) editor for the ONLINE ordering menu.
 *
 * Square is the source of truth for what items exist and their price; this page
 * edits a presentation overlay (onlineCatalog/{env}) that only changes how the
 * menu LOOKS online — display name, description, photo, badges, section, order,
 * and show/hide. Prices are read-only here (change them in Square). Every edit
 * writes to Firestore and the /order screen reflects it within seconds.
 *
 * Design mirrors a DoorDash/Uber Eats "Menu Manager": searchable list grouped
 * by section, per-item editor, and up/down reordering for items and sections.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth } from '../../firebase';
import useSquareMenu from '../NewMenu/hooks/useSquareMenu';
import {
  adminGetCatalogOverlay, adminCatalogAction, compressImageFile,
} from '../NewMenu/ordering/orderApi';
import './MenuManager.css';

const cents$ = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const BADGES = [
  { key: 'popular',    label: 'Popular',     emoji: '🔥' },
  { key: 'new',        label: 'New',         emoji: '✨' },
  { key: 'bestseller', label: 'Best seller', emoji: '⭐' },
  { key: 'chef',       label: "Chef's pick", emoji: '👨‍🍳' },
  { key: 'spicy',      label: 'Spicy',       emoji: '🌶️' },
  { key: 'veg',        label: 'Veg',         emoji: '🟢' },
  { key: 'glutenfree', label: 'Gluten-free', emoji: '🌾' },
];
const badgeMeta = (k) => BADGES.find(b => b.key === k);

const itemPriceCents = (it) => {
  const inStock = (it.variations || []).filter(v => !v.soldOut);
  return (inStock[0] || (it.variations || [])[0])?.priceCents ?? it.basePriceCents ?? 0;
};

const rid = (p) => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

// ── Option-group builder (dosa type, add-ons like ghee/onion/podi) ────────────
function OptionGroupsEditor({ groups, onChange }) {
  const setGroup = (gi, patch) => onChange(groups.map((g, i) => i === gi ? { ...g, ...patch } : g));
  const setOption = (gi, oi, patch) => setGroup(gi, {
    options: groups[gi].options.map((o, i) => i === oi ? { ...o, ...patch } : o),
  });

  const addGroup = () => onChange([...groups, { id: rid('g'), name: '', type: 'single', required: false, min: 0, max: 0, options: [{ id: rid('o'), name: '', priceCents: 0 }] }]);
  const removeGroup = (gi) => onChange(groups.filter((_, i) => i !== gi));
  const addOption = (gi) => setGroup(gi, { options: [...groups[gi].options, { id: rid('o'), name: '', priceCents: 0 }] });
  const removeOption = (gi, oi) => setGroup(gi, { options: groups[gi].options.filter((_, i) => i !== oi) });

  return (
    <div className="mm-og">
      {groups.map((g, gi) => (
        <div className="mm-og__group" key={g.id}>
          <div className="mm-og__grouphead">
            <input
              className="mm-og__gname" value={g.name} maxLength={80}
              onChange={e => setGroup(gi, { name: e.target.value })}
              placeholder="Group name (e.g. Dosa type, Add-ons)" />
            <button type="button" className="mm-og__rm" onClick={() => removeGroup(gi)} title="Remove group">✕</button>
          </div>

          <div className="mm-og__rules">
            <div className="mm-og__typetoggle">
              <button type="button" className={g.type === 'single' ? 'on' : ''} onClick={() => setGroup(gi, { type: 'single' })}>Pick one</button>
              <button type="button" className={g.type === 'multi' ? 'on' : ''} onClick={() => setGroup(gi, { type: 'multi' })}>Pick many</button>
            </div>
            {g.type === 'single' ? (
              <label className="mm-og__req">
                <input type="checkbox" checked={g.required === true} onChange={e => setGroup(gi, { required: e.target.checked })} /> Required
              </label>
            ) : (
              <div className="mm-og__minmax">
                <label>Min <input type="number" min="0" value={g.min ?? 0} onChange={e => setGroup(gi, { min: parseInt(e.target.value, 10) || 0 })} /></label>
                <label>Max <input type="number" min="1" value={g.max || g.options.length} onChange={e => setGroup(gi, { max: parseInt(e.target.value, 10) || 0 })} /></label>
              </div>
            )}
          </div>

          {g.options.map((o, oi) => (
            <div className="mm-og__opt" key={o.id}>
              <input className="mm-og__oname" value={o.name} maxLength={80}
                onChange={e => setOption(gi, oi, { name: e.target.value })} placeholder="Option (e.g. Ghee, Onion, Podi)" />
              <div className="mm-og__price">
                <span>+$</span>
                <input type="number" min="0" step="0.25"
                  value={o.priceCents ? (o.priceCents / 100) : ''}
                  onChange={e => setOption(gi, oi, { priceCents: Math.round((parseFloat(e.target.value) || 0) * 100) })}
                  placeholder="0.00" />
              </div>
              <button type="button" className="mm-og__rm" onClick={() => removeOption(gi, oi)} title="Remove option">✕</button>
            </div>
          ))}
          <button type="button" className="mm-btn mm-btn--ghost mm-og__add" onClick={() => addOption(gi)}>+ Add option</button>
        </div>
      ))}
      <button type="button" className="mm-btn mm-btn--ghost" onClick={addGroup}>+ Add option group</button>
    </div>
  );
}

// ── Image uploader (drag-drop / click, compresses client-side) ────────────────
function ImageUploader({ item, currentUrl, busy, onUpload, onRemove, onApplyUrl }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setErr(''); setUploading(true);
    try { await onUpload(file); }
    catch (e) { setErr(e.message || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const remove = async () => {
    setErr(''); setUploading(true);
    try { await onRemove(); } catch (e) { setErr(e.message || 'Remove failed'); } finally { setUploading(false); }
  };
  const applyUrl = async () => {
    setErr(''); setUploading(true);
    try { await onApplyUrl(url.trim()); setShowUrl(false); setUrl(''); }
    catch (e) { setErr(e.message || 'Failed'); } finally { setUploading(false); }
  };

  return (
    <div className="mm-field">
      <span>Photo</span>
      <div
        className={`mm-drop${dragOver ? ' mm-drop--over' : ''}${currentUrl ? ' mm-drop--has' : ''}`}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        onClick={() => !uploading && inputRef.current?.click()}
        role="button" tabIndex={0}>
        {uploading ? (
          <div className="mm-drop__busy"><span className="mm-spin" /> Working…</div>
        ) : currentUrl ? (
          <img className="mm-drop__img" src={currentUrl} alt={item.name} />
        ) : (
          <div className="mm-drop__empty">
            <span className="mm-drop__icon">📷</span>
            <span>Drag a photo here, or click to upload</span>
            <small>JPG / PNG / WebP · auto-compressed</small>
          </div>
        )}
        <input ref={inputRef} type="file" accept="image/*" hidden
          onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ''; }} />
      </div>
      {err && <small className="mm-drop__err">⚠ {err}</small>}
      <div className="mm-drop__actions">
        <button type="button" className="mm-btn mm-btn--ghost" onClick={() => inputRef.current?.click()} disabled={uploading || busy}>
          {currentUrl ? 'Replace' : 'Upload'}
        </button>
        {currentUrl && (
          <button type="button" className="mm-btn mm-btn--ghost mm-drop__rm" onClick={remove} disabled={uploading || busy}>Remove</button>
        )}
        <button type="button" className="mm-drop__urltoggle" onClick={() => setShowUrl(s => !s)}>{showUrl ? 'Cancel' : 'Paste URL'}</button>
      </div>
      {showUrl && (
        <div className="mm-drop__urlrow">
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…/photo.jpg" />
          <button type="button" className="mm-btn mm-btn--primary" onClick={applyUrl} disabled={uploading || !url.trim()}>Apply</button>
        </div>
      )}
    </div>
  );
}

// ── Item editor drawer ────────────────────────────────────────────────────────
function ItemEditor({ item, override, categories, busy, onSave, onReset, onClose, onUploadPhoto, onRemovePhoto, onApplyPhotoUrl }) {
  const [name, setName]       = useState(override.nameOverride || '');
  const [desc, setDesc]       = useState(override.description || '');
  const [category, setCategory] = useState(override.categoryOverride || item.categoryId || '');
  const [badges, setBadges]   = useState(Array.isArray(override.badges) ? override.badges : []);
  const [hidden, setHidden]   = useState(override.hidden === true);
  const [groups, setGroups]   = useState(Array.isArray(override.optionGroups) ? override.optionGroups : []);

  const toggleBadge = (k) => setBadges(b => b.includes(k) ? b.filter(x => x !== k) : [...b, k]);
  const currentPhoto = override.photoUrl || item.imageUrl || '';

  const save = () => {
    // Diff against the current override so we only send what changed (merge-safe).
    // Photo is handled by its own immediate upload/remove actions, not here.
    const patch = {};
    const trim = (s) => s.trim();
    if (trim(name) !== (override.nameOverride || '')) patch.nameOverride = trim(name) || null;
    if (trim(desc) !== (override.description || '')) patch.description = trim(desc) || null;
    if ((category || '') !== (override.categoryOverride || item.categoryId || '')) {
      patch.categoryOverride = category && category !== item.categoryId ? category : null;
    }
    const curBadges = Array.isArray(override.badges) ? override.badges : [];
    if (JSON.stringify(badges) !== JSON.stringify(curBadges)) patch.badges = badges;
    if (hidden !== (override.hidden === true)) patch.hidden = hidden;
    const curGroups = Array.isArray(override.optionGroups) ? override.optionGroups : [];
    if (JSON.stringify(groups) !== JSON.stringify(curGroups)) patch.optionGroups = groups;
    if (Object.keys(patch).length === 0) { onClose(); return; }
    onSave(item.id, patch);
  };

  return (
    <div className="mm-overlay" onClick={onClose}>
      <aside className="mm-drawer" onClick={e => e.stopPropagation()} role="dialog" aria-label={`Edit ${item.name}`}>
        <div className="mm-drawer__head">
          <h3>Edit item</h3>
          <button className="mm-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="mm-drawer__body">
          <div className="mm-preview">
            <div className="mm-preview__thumb" style={currentPhoto ? { backgroundImage: `url(${currentPhoto})` } : undefined}>
              {!currentPhoto && <span>🍽️</span>}
            </div>
            <div>
              <div className="mm-preview__name">{name.trim() || item.name}</div>
              <div className="mm-preview__price">{cents$(itemPriceCents(item))} · from Square</div>
            </div>
          </div>

          <label className="mm-field">
            <span>Display name</span>
            <input value={name} onChange={e => setName(e.target.value)} placeholder={item.name} maxLength={120} />
            <small>Leave blank to use Square's name.</small>
          </label>

          <label className="mm-field">
            <span>Description</span>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Short, appetizing description…" rows={3} maxLength={500} />
          </label>

          <ImageUploader
            item={item}
            currentUrl={currentPhoto}
            busy={busy}
            onUpload={(file) => onUploadPhoto(item.id, file)}
            onRemove={() => onRemovePhoto(item.id)}
            onApplyUrl={(u) => onApplyPhotoUrl(item.id, u)}
          />

          <label className="mm-field">
            <span>Section</span>
            <select value={category} onChange={e => setCategory(e.target.value)}>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>

          <div className="mm-field">
            <span>Badges</span>
            <div className="mm-badges">
              {BADGES.map(b => (
                <button
                  key={b.key} type="button"
                  className={`mm-badge${badges.includes(b.key) ? ' mm-badge--on' : ''}`}
                  onClick={() => toggleBadge(b.key)}>
                  {b.emoji} {b.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mm-field">
            <span>Options &amp; add-ons</span>
            <small style={{ marginBottom: 8 }}>Let customers pick a type (e.g. Dosa type) or add extras (Ghee, Onion, Podi). “Pick one” = choose a single option; “Pick many” = multi-select.</small>
            <OptionGroupsEditor groups={groups} onChange={setGroups} />
          </div>

          <label className="mm-toggle">
            <input type="checkbox" checked={hidden} onChange={e => setHidden(e.target.checked)} />
            <span>Hide this item from the online menu</span>
          </label>
        </div>

        <div className="mm-drawer__foot">
          {override.itemId && (
            <button className="mm-btn mm-btn--ghost" onClick={() => onReset(item.id)} disabled={busy}>Reset to Square</button>
          )}
          <div className="mm-drawer__foot-right">
            <button className="mm-btn mm-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="mm-btn mm-btn--primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ── Item row ──────────────────────────────────────────────────────────────────
function ItemRow({ item, override, onEdit, onMoveUp, onMoveDown, first, last, busy }) {
  const hidden = item.ecomVisible === false;
  return (
    <div className={`mm-row${hidden ? ' mm-row--hidden' : ''}`}>
      <div className="mm-row__reorder">
        <button onClick={onMoveUp} disabled={first || busy} aria-label="Move up">▲</button>
        <button onClick={onMoveDown} disabled={last || busy} aria-label="Move down">▼</button>
      </div>
      <div className="mm-row__thumb" style={item.imageUrl ? { backgroundImage: `url(${item.imageUrl})` } : undefined}>
        {!item.imageUrl && <span>🍽️</span>}
      </div>
      <div className="mm-row__main">
        <div className="mm-row__name">
          {item.name}
          {override.itemId && <span className="mm-tag mm-tag--custom">Customized</span>}
          {hidden && <span className="mm-tag mm-tag--hidden">Hidden</span>}
          {item.soldOut && <span className="mm-tag mm-tag--out">Sold out</span>}
        </div>
        {item.description && <div className="mm-row__desc">{item.description}</div>}
        <div className="mm-row__meta">
          <span className="mm-row__price">{cents$(itemPriceCents(item))}</span>
          {(item.badges || []).map(b => {
            const m = badgeMeta(b);
            return m ? <span key={b} className="mm-row__badge">{m.emoji} {m.label}</span> : null;
          })}
        </div>
      </div>
      <button className="mm-btn mm-btn--ghost mm-row__edit" onClick={onEdit} disabled={busy}>Edit</button>
    </div>
  );
}

export default function MenuManager() {
  const { categories, loading, error, refetch } = useSquareMenu();
  const [overlay, setOverlay] = useState({ items: {}, categories: {} });
  const [editing, setEditing] = useState(null); // item id being edited
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [customizedOnly, setCustomizedOnly] = useState(false);

  const itemOvById = useMemo(() => {
    const m = {}; Object.values(overlay.items || {}).forEach(v => { if (v?.itemId) m[v.itemId] = v; }); return m;
  }, [overlay]);
  const catOvById = useMemo(() => {
    const m = {}; Object.values(overlay.categories || {}).forEach(v => { if (v?.categoryId) m[v.categoryId] = v; }); return m;
  }, [overlay]);

  const token = () => auth.currentUser?.getIdToken();

  const loadOverlay = useCallback(async () => {
    try {
      const idToken = await token();
      if (!idToken) throw new Error('Sign in again to manage the menu.');
      setOverlay(await adminGetCatalogOverlay(idToken));
    } catch (e) { setErr(e.message || 'Could not load menu overrides'); }
  }, []);

  useEffect(() => { loadOverlay(); }, [loadOverlay]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };

  const act = useCallback(async (payload, successMsg) => {
    setBusy(true); setErr('');
    try {
      const idToken = await token();
      await adminCatalogAction(payload, idToken);
      await Promise.all([loadOverlay(), refetch(true)]);
      if (successMsg) flash(successMsg);
      return true;
    } catch (e) { setErr(e.message || 'Update failed'); return false; }
    finally { setBusy(false); }
  }, [loadOverlay, refetch]);

  const saveItem = async (itemId, patch) => {
    const ok = await act({ action: 'upsert-item', itemId, patch }, 'Saved');
    if (ok) setEditing(null);
  };
  const resetItem = async (itemId) => {
    const ok = await act({ action: 'reset-item', itemId }, 'Reset to Square');
    if (ok) setEditing(null);
  };
  const reorderItems = (categoryId, orderedItemIds) => act({ action: 'reorder', categoryId, orderedItemIds });
  const reorderCats  = (orderedCategoryIds) => act({ action: 'reorder-categories', orderedCategoryIds });
  const saveCategory = (categoryId, patch) => act({ action: 'upsert-category', categoryId, patch }, 'Section updated');

  // Photo actions run immediately (compress client-side, then upload). They
  // re-throw so the uploader can surface its own inline error + spinner.
  const runPhoto = useCallback(async (fn, okMsg) => {
    setBusy(true); setErr('');
    try {
      const idToken = await token();
      await fn(idToken);
      await Promise.all([loadOverlay(), refetch(true)]);
      if (okMsg) flash(okMsg);
    } catch (e) { setErr(e.message || 'Photo update failed'); throw e; }
    finally { setBusy(false); }
  }, [loadOverlay, refetch]);

  const uploadPhoto = (itemId, file) => runPhoto(async (idToken) => {
    const dataUrl = await compressImageFile(file);
    await adminCatalogAction({ action: 'upload-photo', itemId, dataUrl }, idToken);
  }, 'Photo updated');
  const removePhoto = (itemId) => runPhoto(async (idToken) => {
    await adminCatalogAction({ action: 'remove-photo', itemId }, idToken);
  }, 'Photo removed');
  const applyPhotoUrl = (itemId, photoUrl) => runPhoto(async (idToken) => {
    await adminCatalogAction({ action: 'upsert-item', itemId, patch: { photoUrl: photoUrl || null } }, idToken);
  }, 'Photo updated');

  // Categories as shown (merged order). Show ALL items incl. hidden for editing.
  const cats = useMemo(() => (categories || []).filter(c => (c.items || []).length > 0), [categories]);

  // At-a-glance stats for the header.
  const stats = useMemo(() => {
    const all = cats.flatMap(c => c.items);
    return {
      items: all.length,
      customized: all.filter(i => itemOvById[i.id]).length,
      withPhoto: all.filter(i => i.imageUrl).length,
      hidden: all.filter(i => i.ecomVisible === false).length,
    };
  }, [cats, itemOvById]);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0 || customizedOnly;
  const matches = (i) => (!q || i.name.toLowerCase().includes(q)) && (!customizedOnly || !!itemOvById[i.id]);
  const filteredFlat = useMemo(() => {
    if (!searching) return [];
    return cats.flatMap(c => (c.items || []).filter(matches).map(i => ({ item: i, cat: c })));
  }, [cats, q, customizedOnly, searching, itemOvById]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveItem = (cat, idx, dir) => {
    const ids = cat.items.map(i => i.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorderItems(cat.id, ids);
  };
  const moveCat = (idx, dir) => {
    const ids = cats.map(c => c.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorderCats(ids);
  };

  const editingItem = editing ? cats.flatMap(c => c.items).find(i => i.id === editing) : null;

  return (
    <div className="mm-page">
      <header className="mm-header">
        <div>
          <h1>Menu Manager</h1>
          <p>Customize how the online menu looks. Prices &amp; items come from Square.</p>
        </div>
        <button className="mm-btn mm-btn--ghost" onClick={() => { refetch(true); loadOverlay(); }} disabled={busy}>↻ Refresh</button>
      </header>

      {!loading && !error && (
        <div className="mm-stats">
          <span className="mm-stat"><strong>{stats.items}</strong> items</span>
          <span className="mm-stat"><strong>{stats.customized}</strong> customized</span>
          <span className="mm-stat"><strong>{stats.withPhoto}</strong> with photo</span>
          {stats.hidden > 0 && <span className="mm-stat mm-stat--muted"><strong>{stats.hidden}</strong> hidden</span>}
        </div>
      )}

      <div className="mm-toolbar">
        <div className="mm-search">
          <span aria-hidden="true">🔍</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search items…" />
          {query && <button onClick={() => setQuery('')} aria-label="Clear">✕</button>}
        </div>
        <button
          className={`mm-filter${customizedOnly ? ' mm-filter--on' : ''}`}
          onClick={() => setCustomizedOnly(v => !v)}>
          ✎ Customized only
        </button>
      </div>

      {err && <div className="mm-alert mm-alert--err">⚠ {err}</div>}
      {msg && <div className="mm-alert mm-alert--ok">✓ {msg}</div>}

      {loading ? (
        <div className="mm-skeletons">
          {Array.from({ length: 6 }).map((_, i) => <div className="mm-skel" key={i} />)}
        </div>
      ) : error ? (
        <div className="mm-state mm-state--err">⚠ {error} <button className="mm-btn mm-btn--ghost" onClick={() => refetch(true)}>Retry</button></div>
      ) : searching ? (
        <div className="mm-section">
          <div className="mm-section__head"><h2>{customizedOnly && !q ? 'Customized items' : `Results for “${query}”`}</h2></div>
          {filteredFlat.length === 0
            ? <div className="mm-state">No items match.</div>
            : filteredFlat.map(({ item }) => (
                <ItemRow key={item.id} item={item} override={itemOvById[item.id] || {}}
                  onEdit={() => setEditing(item.id)} first last busy={busy} />
              ))}
        </div>
      ) : (
        cats.map((cat, ci) => {
          const cov = catOvById[cat.id] || {};
          return (
            <div className={`mm-section${cat.hidden ? ' mm-section--hidden' : ''}`} key={cat.id}>
              <div className="mm-section__head">
                <div className="mm-section__reorder">
                  <button onClick={() => moveCat(ci, -1)} disabled={ci === 0 || busy} aria-label="Section up">▲</button>
                  <button onClick={() => moveCat(ci, 1)} disabled={ci === cats.length - 1 || busy} aria-label="Section down">▼</button>
                </div>
                <h2>{cat.name}{cat.hidden && <span className="mm-tag mm-tag--hidden">Hidden</span>}</h2>
                <button
                  className="mm-btn mm-btn--ghost mm-section__hide"
                  onClick={() => saveCategory(cat.id, { hidden: !(cov.hidden === true) })}
                  disabled={busy}>
                  {cov.hidden === true ? 'Show section' : 'Hide section'}
                </button>
              </div>
              {cat.items.map((item, ii) => (
                <ItemRow
                  key={item.id} item={item} override={itemOvById[item.id] || {}}
                  onEdit={() => setEditing(item.id)}
                  onMoveUp={() => moveItem(cat, ii, -1)}
                  onMoveDown={() => moveItem(cat, ii, 1)}
                  first={ii === 0} last={ii === cat.items.length - 1} busy={busy}
                />
              ))}
            </div>
          );
        })
      )}

      {editingItem && (
        <ItemEditor
          item={editingItem}
          override={itemOvById[editingItem.id] || {}}
          categories={cats}
          busy={busy}
          onSave={saveItem}
          onReset={resetItem}
          onClose={() => setEditing(null)}
          onUploadPhoto={uploadPhoto}
          onRemovePhoto={removePhoto}
          onApplyPhotoUrl={applyPhotoUrl}
        />
      )}
    </div>
  );
}
